import { apiJson, apiRequest } from '../../shared/api/http';

export type LegalDocId = 'privacy' | 'terms';

export interface LegalDoc {
  id: LegalDocId;
  title: string;
  body: string;
  version: number;
  draft: boolean;
  updated_at: string | null;
  updated_by_name?: string | null;
  edited?: boolean;
}

export interface LegalStatus {
  notice_version: number;
  terms_version: number;
  acknowledged: boolean;
  first_time: boolean;
  acknowledged_at: string | null;
  privacy_review_due: boolean;
  shown?: { email: boolean; points: boolean; kudos: boolean; birthday: boolean };
}

export interface LegalHistoryItem {
  id: string;
  version: number;
  new_version: boolean;
  title: string;
  body: string;
  draft: boolean;
  changed_at: string | null;
  changed_by_name: string | null;
}

/** Public: the sign-in page links here before anyone has an account. */
export function fetchLegalDoc(id: LegalDocId): Promise<LegalDoc> {
  return apiJson(`/api/legal/${id}`);
}

export function fetchLegalStatus(): Promise<LegalStatus> {
  return apiJson('/api/legal/status', { auth: true });
}

export function acknowledgeLegal(noticeVersion: number, termsVersion: number): Promise<LegalStatus> {
  return apiJson('/api/legal/acknowledge', {
    method: 'POST', auth: true, body: { notice_version: noticeVersion, terms_version: termsVersion },
  });
}

export function answerPrivacyReview(outcome: 'reviewed' | 'kept'): Promise<LegalStatus> {
  return apiJson('/api/users/me/privacy-review', { method: 'POST', auth: true, body: { outcome } });
}

export function fetchAdminLegalDoc(id: LegalDocId): Promise<LegalDoc> {
  return apiJson(`/api/admin/legal/${id}`, { auth: true });
}

export function saveLegalDoc(id: LegalDocId, body: { title: string; body: string; new_version: boolean; draft: boolean }): Promise<LegalDoc> {
  return apiJson(`/api/admin/legal/${id}`, { method: 'PUT', auth: true, body });
}

export function fetchLegalHistory(id: LegalDocId): Promise<LegalHistoryItem[]> {
  return apiJson(`/api/admin/legal/${id}/history`, { auth: true });
}

export function fetchAcknowledgementSummary(): Promise<{ people: number; acknowledged: number; notice_version: number; terms_version: number }> {
  return apiJson('/api/admin/legal/acknowledgements', { auth: true });
}

/** Download my data: the server's JSON, saved as a file. Throws with the server's reason (for example the hourly limit). */
export async function downloadMyData(): Promise<void> {
  const response = await apiRequest('/api/users/me/export', { auth: true });
  if (!response.ok) {
    let message = '';
    try { const p = await response.json(); message = p?.error || p?.message || ''; } catch { /* not JSON */ }
    const error: any = new Error(message || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `grow-my-data-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
